import React from 'react';
import { useRouteMatch } from 'react-router';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useOpportunity, useCategories } from './hooks/useContentMarketingQueries';
import { ContentImage } from './components/ContentImage';
import { formatDateDisplay } from 'utils/date';
import { SvgIcon } from 'ui/SvgIcon';
import CalendarIcon from 'ui/Icons/calendar.component.svg';
import LocationIcon from 'ui/Icons/location.component.svg';
import NewReleasesIcon from 'ui/Icons/new-releases.component.svg';
import { MediaGallery } from 'ui/MediaGallery';
import { DocumentLinks } from 'ui/DocumentLinks';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import { useSelector } from 'react-redux';
import { getMainCompanyLogoSelector } from 'store/modules/bootstrap/selectors';
import { getCurrencySymbol } from 'utils';
import { TCurrencyCode } from 'interfaces';
import { appendQueryParam } from 'utils/url';

interface IContentDetailPageRouteParams {
  contentUuid: string;
}

export const ContentDetailPage = () => {
  const match = useRouteMatch<IContentDetailPageRouteParams>();
  const { contentUuid } = match.params;

  const { data, isLoading, isError } = useOpportunity(contentUuid);
  const { data: categories } = useCategories();
  const params = useDynamicParameters();
  const { MARKETING_MANAGER_BASE_URL } = params.dynamicParameters;
  const mainCompanyLogo = useSelector(getMainCompanyLogoSelector);

  if (isLoading) {
    return <LoadingBar />;
  }

  if (isError) {
    return (
      <div className="mx-[20px] crm-desktop:mx-[80px] mt-8">
        <ErrorBar message="Failed to load content. Please try again." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-[20px] crm-desktop:mx-[80px] mt-8">
        <ErrorBar message="Content not found." />
      </div>
    );
  }

  // Map category UUIDs to category objects
  const categoryList = categories
    ? data.categoryUuids
        .map(uuid => categories.find(cat => cat.uuid === uuid))
        .filter((cat): cat is NonNullable<typeof cat> => cat !== undefined)
    : [];

  // Check if Details panel has any content
  const hasDetailsContent =
    (data.stayPeriodStart && data.stayPeriodEnd) ||
    data.discountPercent ||
    data.discountedPrice ||
    data.fullPrice ||
    (data.bookingStart && data.bookingEnd);

  // Check if right column should be shown
  const showRightColumn = hasDetailsContent || data.contactEmail;

  return (
    <div>
      {/* Main Company Logo */}
      {mainCompanyLogo?.url && (
        <div className="w-full bg-white flex items-center justify-center py-[30px]">
          <img src={appendQueryParam(mainCompanyLogo.url, 'isIcon', 'true')} alt="Company Logo" className="max-h-[40px]" />
        </div>
      )}

      {/* Hero Image - Full width, no margins */}
      {data.heroImage && (
        <div className="relative w-full h-[390px] overflow-hidden">
          <ContentImage className="h-full" index={0} upload={data.heroImage} />

          {/* Exclusive Badge - Top left corner */}
          {data.isExclusive && (
            <div
              className="absolute top-20px left-20px bg-brown-prime text-white font-hurmegeometric-sans font-[600] text-[18px] text-center px-10px py-5px"
              style={{ letterSpacing: '0.05em', lineHeight: '1.2em' }}
            >
              Exclusive
            </div>
          )}
        </div>
      )}

      <div className="mx-[20px] crm-desktop:mx-[80px]">
        {/* Top Content Section */}
        <div className=" flex flex-col gap-[10px] mt-[20px]">
          {/* Metadata row */}
          <div className="flex flex-row items-center gap-[20px]">
            {/* Published date */}
            {data.activeFrom && (
              <div className="flex flex-row items-center gap-[5px]">
                <SvgIcon IconComponent={CalendarIcon} wrapperClass="w-[18px]" className="w-[14px] fill-gray-80" />
                <span className="text-14px text-gray-80 uppercase font-hurmegeometric-sans">
                  {formatDateDisplay(data.activeFrom)}
                </span>
              </div>
            )}

            {/* Destinations */}
            {data.destinations && data.destinations.length > 0 && (
              <div className="flex flex-row items-center gap-[5px]">
                <SvgIcon
                  IconComponent={LocationIcon}
                  wrapperClass="w-[18px]"
                  className="w-[11px] mt-[2px] fill-gray-80"
                />
                <span className="text-14px text-gray-80 uppercase font-hurmegeometric-sans">
                  {data.destinations.join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Title */}
          <span className="text-4xl text-brown-prime font-noe-display block" style={{ lineHeight: '1.276em' }}>
            {data.title}
          </span>

          {/* Categories and Discount */}
          {(categoryList.length > 0 || data.discountPercent) && (
            <div className="flex flex-row gap-[5px]">
              {categoryList.map(category => (
                <div
                  key={category.uuid}
                  className="flex flex-col justify-center items-center gap-[2px] px-10px py-5px bg-white border border-gray-20 font-hurmegeometric-sans text-14px text-status-enquiry text-center"
                  style={{ lineHeight: '1.272em' }}
                >
                  {category.value}
                </div>
              ))}

              {/* Discount badge */}
              {data.discountPercent && (
                <div className="flex flex-row justify-center items-center gap-[5px] px-10px py-5px bg-white border border-gray-20 h-[30px]">
                  <span
                    className="font-hurmegeometric-sans text-14px text-red-92 text-center"
                    style={{ lineHeight: '1.272em' }}
                  >
                    Save
                  </span>
                  <span
                    className="font-hurmegeometric-sans text-20px text-red-92 uppercase text-center"
                    style={{ lineHeight: '1.272em' }}
                  >
                    -{data.discountPercent}%
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Image Gallery */}
        {data.photos && data.photos.length > 0 && (
          <div className="mt-[40px]">
            <MediaGallery
              uploads={data.photos}
              titleForModal={data.title}
              baseUrlPath={`${MARKETING_MANAGER_BASE_URL}/opportunity/v1/media/`}
            />
          </div>
        )}

        {/* Two-Column Layout */}
        <div className="mt-[40px]">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] 2xl:grid-cols-[1fr_400px] gap-[20px] crm-desktop:gap-[50px]">
            {/* Left Column - Content sections and documents */}
            <div>
              {/* Content Sections (includes shortDescription as first section) */}
              <div className="flex flex-col gap-[40px]">
                {/* Short Description - Always first section */}
                <div className="flex flex-col gap-[10px]">
                  {/* Section Title */}
                  <span
                    className="font-noe-display text-26px text-brown-prime font-normal"
                    style={{ lineHeight: '1.276em' }}
                  >
                    {data.shortDescription.title}
                  </span>
                  {/* Section Body */}
                  <HtmlStringRenderer
                    content={data.shortDescription.content}
                    className="font-hurmegeometric-sans text-14px text-text-colour"
                    style={{ lineHeight: '1.571em' }}
                  />
                </div>

                {/* Additional Sections */}
                {data.sections && data.sections.length > 0 && (
                  <>
                    {data.sections.map((section, index) => (
                      <div key={`section-${index}`} className="flex flex-col gap-[10px]">
                        {/* Section Title */}
                        <span
                          className="font-noe-display text-26px text-brown-prime font-normal"
                          style={{ lineHeight: '1.276em' }}
                        >
                          {section.title}
                        </span>
                        {/* Section Body */}
                        <HtmlStringRenderer
                          content={section.content}
                          className="font-hurmegeometric-sans text-14px text-text-colour"
                          style={{ lineHeight: '1.571em' }}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Supporting Documents */}
              {data.documents && data.documents.length > 0 && (
                <DocumentLinks
                  documents={data.documents}
                  baseUrlPath={`${MARKETING_MANAGER_BASE_URL}/opportunity/v1/media/`}
                  title="Supporting Documents"
                  className="mt-[40px]"
                />
              )}
            </div>

            {/* Right Column - Information panel */}
            {showRightColumn && (
              <div className="">
                {/* Details Panel */}
                {hasDetailsContent && (
                  <div className="bg-brown-prime-10 p-[30px] flex flex-col gap-[15px]">
                    {/* Title */}
                    <span
                      className="font-noe-display text-26px text-brown-prime font-normal"
                      style={{ lineHeight: '1.276em' }}
                    >
                      Details
                    </span>

                    {/* Stay Period and Discount */}
                    <div className="flex flex-col gap-[5px]">
                      {/* Stay Period */}
                      {data.stayPeriodStart && data.stayPeriodEnd && (
                        <div className="flex flex-row items-center gap-[5px]">
                          <SvgIcon
                            IconComponent={CalendarIcon}
                            wrapperClass="w-[24px]"
                            className="w-[18px] fill-brown-prime"
                          />
                          <span
                            className="font-hurmegeometric-sans text-14px text-flint uppercase font-semibold flex-1"
                            style={{ lineHeight: '1.2em' }}
                          >
                            Stay Between {formatDateDisplay(data.stayPeriodStart)} and{' '}
                            {formatDateDisplay(data.stayPeriodEnd)}
                          </span>
                        </div>
                      )}

                      {/* Discount Applied */}
                      {data.discountPercent && (
                        <div className="flex flex-row items-center gap-[5px]">
                          <SvgIcon IconComponent={NewReleasesIcon} className="w-[24px] fill-brown-prime" />
                          <span
                            className="font-hurmegeometric-sans text-14px text-flint uppercase"
                            style={{ lineHeight: '1.272em' }}
                          >
                            Discount applied <span className="text-red-92">-{data.discountPercent}%</span>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Pricing */}
                    {(data.discountedPrice || data.fullPrice) && (
                      <div className="flex flex-col">
                        {/* Prices Row */}
                        <div className="flex flex-row items-center gap-[10px]">
                          {/* Discounted Price */}
                          {data.discountedPrice && (
                            <span
                              className="font-hurmegeometric-sans text-[24px] text-red-92 uppercase"
                              style={{ lineHeight: '1.272em' }}
                            >
                              {getCurrencySymbol((data.currency || 'USD') as TCurrencyCode)}
                              {data.discountedPrice.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          )}
                          {/* Full Price (strikethrough) */}
                          {data.fullPrice && (
                            <span
                              className="font-hurmegeometric-sans text-14px text-gray-100 uppercase line-through"
                              style={{ lineHeight: '1.272em' }}
                            >
                              {getCurrencySymbol((data.currency || 'USD') as TCurrencyCode)}
                              {data.fullPrice.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          )}
                        </div>
                        {/* You Save */}
                        {data.discountedPrice && data.fullPrice && data.fullPrice > data.discountedPrice && (
                          <span
                            className="font-hurmegeometric-sans text-14px text-red-92"
                            style={{ lineHeight: '1.286em' }}
                          >
                            You Save {getCurrencySymbol((data.currency || 'USD') as TCurrencyCode)}
                            {(data.fullPrice - data.discountedPrice).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Booking Period */}
                    {data.bookingStart && data.bookingEnd && (
                      <span
                        className="font-hurmegeometric-sans text-14px text-gray-100"
                        style={{ lineHeight: '1.272em' }}
                      >
                        Only for bookings made from {formatDateDisplay(data.bookingStart)} to{' '}
                        {formatDateDisplay(data.bookingEnd)}
                      </span>
                    )}
                  </div>
                )}

                {/* Contact Us Section */}
                {data.contactEmail && (
                  <div className="bg-brown-prime-10 p-[30px] flex flex-col gap-[15px] mt-[20px]">
                    {/* Title */}
                    <span
                      className="font-noe-display text-26px text-brown-prime font-normal"
                      style={{ lineHeight: '1.276em' }}
                    >
                      Contact us
                    </span>

                    {/* Description */}
                    <span className="font-hurmegeometric-sans text-14px text-black" style={{ lineHeight: '1.571em' }}>
                      Contact our luxury travel specialists:
                    </span>

                    {/* Contact Button */}
                    <a
                      href={`mailto:${data.contactEmail}?subject=${encodeURIComponent(
                        `Enquiry about: ${data.title}`
                      )}&body=${encodeURIComponent(
                        'I would like to receive more information about the mentioned content'
                      )}`}
                      className="bg-brown-prime hover:bg-brown-hard font-hurmegeometric-sans font-semibold text-14px uppercase text-center py-[8px] px-[20px] h-[35px] flex items-center justify-center transition-colors text-white-true"
                      style={{ letterSpacing: '0.05em', lineHeight: '1.2em' }}
                    >
                      Contact Now
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
